/**
 * compassionateFatigueEngine.js
 *
 * Detects fatigue from CONSTANTLY HOLDING OTHERS' PAIN.
 *
 * NOT: general tiredness (pastoralExhaustionDetector handles that)
 * NOT: emotional overload of the person being helped (emotionalCapacityEvaluator)
 * THIS: what happens when a pastor has been absorbing grief, trauma,
 *       crisis, and suffering from others for an extended period —
 *       leading to emotional depletion, protective distancing, or affective hardening.
 *
 * Types (ordered by severity):
 *   ninguna                — no compassionate fatigue signals
 *   cansancio_emocional    — emotional tiredness from caring
 *   desgaste_compasivo     — cumulative fatigue from holding others' suffering
 *   desconexión_afectiva   — beginning to emotionally distance as protection
 *   endurecimiento         — emotional numbing has set in
 */

const COMPASSION_SIGNALS = [
  // Emotional tiredness from caring (weight: 1)
  {
    weight: 1,
    patterns: [
      /(?:es\s+(?:desgastante|agotador|pesado)\s+(?:escuchar|acompañar|sostener)\s+(?:tanto|tanto\s+dolor|tanto\s+sufrimiento))/i,
      /(?:salgo\s+(?:de\s+esas\s+visitas|del\s+ministerio|de\s+acompañar)\s+(?:muy\s+)?(?:agotado|vaciado|cansado))/i,
      /(?:absorbo\s+(?:el\s+dolor|el\s+sufrimiento|las\s+cargas)\s+(?:de\s+los\s+demás|de\s+todos|de\s+la\s+gente))/i,
    ],
  },
  // Carrying others' pain as personal burden (weight: 2)
  {
    weight: 2,
    patterns: [
      /(?:me\s+llevo\s+(?:los\s+problemas|el\s+dolor|las\s+cargas|el\s+peso)\s+(?:de\s+(?:todos|ellos|la\s+gente))\s+(?:a\s+casa|conmigo))/i,
      /(?:no\s+(?:puedo|logro|soy\s+capaz\s+de)\s+soltar\s+(?:lo\s+que\s+(?:escucho|vivo|acompaño)|el\s+dolor\s+ajeno))/i,
      /(?:los\s+problemas\s+(?:de\s+la\s+congregación|de\s+la\s+familia|de\s+los\s+miembros)\s+(?:se\s+convierten\s+en\s+míos|me\s+afectan\s+profundamente|me\s+quitan\s+el\s+sueño))/i,
    ],
  },
  // Beginning to distance or feel less (weight: 3)
  {
    weight: 3,
    patterns: [
      /(?:a\s+veces\s+(?:siento|me\s+doy\s+cuenta)\s+que\s+(?:ya\s+no\s+me\s+(?:afecta|conmueve|impacta)|me\s+es\s+indiferente))/i,
      /(?:(?:me\s+(?:cuesta|resulta)\s+difícil|ya\s+no\s+puedo)\s+(?:sentir|conectar|empatizar)\s+(?:como\s+antes|con\s+(?:ellos|la\s+gente)))/i,
      /(?:estoy\s+(?:emocionalmente|internamente)\s+(?:desconectado|distante|apagado))/i,
    ],
  },
  // Emotional numbing / hardening (weight: 4)
  {
    weight: 4,
    patterns: [
      /(?:ya\s+no\s+(?:me\s+afecta\s+nada|siento\s+nada|me\s+importa\s+como\s+antes))/i,
      /(?:me\s+he\s+(?:vuelto|puesto)\s+(?:duro|insensible|frío)\s+(?:emocionalmente|con\s+la\s+gente|con\s+los\s+problemas))/i,
      /(?:perdí\s+la\s+(?:compasión|ternura|capacidad\s+de\s+sentir)\s+(?:que\s+tenía\s+antes|por\s+los\s+demás))/i,
    ],
  },
];

/**
 * @param {{
 *   text:            string,
 *   emotion:         object|null,
 *   exhaustionState: string,
 *   conversationLength: number,
 * }} params
 * @returns {{
 *   fatigueType:  string,
 *   score:        number,
 *   instruction:  string|null,
 * }}
 */
export function assessCompassionateFatigue({
  text               = "",
  emotion            = null,
  exhaustionState    = "estable",
  conversationLength = 0,
}) {
  const t = text ?? "";
  let score = 0;

  for (const { weight, patterns } of COMPASSION_SIGNALS) {
    if (patterns.some((p) => p.test(t))) score += weight;
  }

  // Amplify when exhaustion already detected
  if (exhaustionState === "desgaste_profundo" || exhaustionState === "saturación_crítica") score += 2;
  else if (exhaustionState === "agotamiento_progresivo") score += 1;

  // High emotional intensity in a long conversation can signal secondary trauma
  if ((emotion?.intensity ?? 0) >= 0.7 && conversationLength > 6) score += 1;

  const fatigueType =
    score >= 6 ? "endurecimiento"        :
    score >= 4 ? "desconexión_afectiva"  :
    score >= 2 ? "desgaste_compasivo"    :
    score >= 1 ? "cansancio_emocional"   :
    "ninguna";

  return {
    fatigueType,
    score: Math.round(score * 10) / 10,
    instruction: _instruction(fatigueType),
  };
}

function _instruction(type) {
  if (type === "endurecimiento") {
    return "El endurecimiento emocional es señal de agotamiento profundo, no de insensibilidad. No añadas más carga emocional.";
  }
  if (type === "desconexión_afectiva") {
    return "El pastor se está distanciando emocionalmente como protección. Evita urgir más empatía — acompaña su necesidad de recuperar.";
  }
  if (type === "desgaste_compasivo") {
    return "El desgaste compasivo es real. No añadas más peso emocional — responde con calma y aligerando.";
  }
  if (type === "cansancio_emocional") {
    return "Hay cansancio emocional por sostener a otros. Responde con ligereza y sin agravar.";
  }
  return null;
}
